import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { X, Check, AlertCircle, Maximize2, CheckCircle, Info, Image as ImageIcon, AlertTriangle } from 'lucide-react';
import UserDetailsModal from './UserDetailsModal';

const STATUS_OPTIONS = [
  { value: 'to be reviewed', label: 'To be Reviewed' },
  { value: 'paid',           label: 'Paid' },
  { value: 'rejected',       label: 'Rejected' },
];

const TABS = [
  { label: 'All',     value: 'all' },
  { label: 'Pending', value: 'to be reviewed' },
  { label: 'Paid',    value: 'paid' },
  { label: 'Rejected',value: 'rejected' },
];

const StatusSelect = ({ value, onChange }) => {
  const v = (value || '').toLowerCase();
  const cls =
    v === 'paid'     ? 'bg-green-500/10 border-green-500/30 text-green-400'  :
    v === 'rejected' ? 'bg-red-500/10 border-red-500/30 text-red-400'        :
                       'bg-amber-500/10 border-amber-500/30 text-amber-400';
  return (
    <select
      value={v}
      onChange={e => onChange(e.target.value)}
      className={`text-xs font-semibold rounded-lg px-3 py-1.5 border cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all appearance-none ${cls}`}
      style={{ backgroundImage: 'none' }}
    >
      {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
};

const Toast = ({ msg }) => {
  if (!msg) return null;
  const ok = msg.type === 'success';
  return (
    <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm mb-5 border ${
      ok ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
    }`}>
      {ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
      {msg.message}
    </div>
  );
};

const DepositRequests = () => {
  const [depositRequests, setDepositRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusChanges, setStatusChanges] = useState({});
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const { user } = useAuth();

  const [lightboxUrl, setLightboxUrl] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { request, nextStatus }

  const openModal = async (userId) => {
    if (!userId) return;
    setIsModalOpen(true);
    setLoadingDetails(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/users/${userId}/details`, { headers: { Authorization: `Bearer ${token}` } });
      setUserDetails(res.data);
    } catch {
      setUserDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeModal = () => { setIsModalOpen(false); setUserDetails(null); };

  useEffect(() => {
    const load = async () => {
      if (!user || user.role !== 'superadmin') { setError('Not authorized.'); setLoading(false); return; }
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/deposits/admin', { headers: { Authorization: `Bearer ${token}` } });
        setDepositRequests(res.data);
        const init = {};
        res.data.forEach(r => { init[r._id] = (r.status || '').toLowerCase(); });
        setStatusChanges(init);
      } catch {
        setError('Failed to load deposit requests.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const handleStatusChange = (id, val) => setStatusChanges(p => ({ ...p, [id]: val }));

  const commitAction = async (request, nextStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/deposits/${request._id}/status`, { status: nextStatus }, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      setDepositRequests(p => p.map(r => r._id === request._id ? { ...r, status: nextStatus } : r));
      setStatusChanges(p => ({ ...p, [request._id]: nextStatus }));
      setNotification({ message: 'Status updated!', type: 'success' });
    } catch {
      setNotification({ message: 'Failed to update status.', type: 'error' });
    }
  };

  const handleSave = (request) => {
    const next = statusChanges[request._id];
    const current = (request.status || '').toLowerCase();
    if (next === current) { setNotification({ message: 'Status unchanged.', type: 'info' }); return; }
    if (next === 'paid') {
      setConfirmAction({ request, nextStatus: next });
    } else {
      commitAction(request, next);
    }
  };

  useEffect(() => {
    if (!notification) return;
    const t = setTimeout(() => setNotification(null), 3500);
    return () => clearTimeout(t);
  }, [notification]);

  const tabCounts = useMemo(() => {
    const counts = { all: depositRequests.length };
    TABS.slice(1).forEach(tab => {
      counts[tab.value] = depositRequests.filter(r => (r.status || '').toLowerCase() === tab.value).length;
    });
    return counts;
  }, [depositRequests]);

  const filtered = useMemo(() => {
    if (activeTab === 'all') return depositRequests;
    return depositRequests.filter(r => (r.status || '').toLowerCase() === activeTab);
  }, [depositRequests, activeTab]);

  if (loading) return (
    <div className="space-y-2 mt-4">
      {[...Array(6)].map((_, i) => <div key={i} className="h-12 rounded-xl bg-white/4 animate-pulse" />)}
    </div>
  );
  if (error) return (
    <div className="flex items-center gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm mt-4">
      <AlertCircle size={14} /> {error}
    </div>
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-white">Deposit Requests</h1>
        <p className="text-sm text-gray-500 mt-0.5">{depositRequests.length} total requests</p>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map(tab => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
              activeTab === tab.value
                ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-400'
                : 'bg-white/4 border-white/8 text-gray-500 hover:text-gray-300 hover:bg-white/6'
            }`}
          >
            {tab.label}
            <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${
              activeTab === tab.value ? 'bg-cyan-500/20 text-cyan-300' : 'bg-white/6 text-gray-600'
            }`}>
              {tabCounts[tab.value] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <Toast msg={notification} />

      <div className="rounded-2xl border border-white/8 overflow-hidden bg-[#0d1526]/60">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-white/6 bg-white/[0.02]">
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">User</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell whitespace-nowrap">Mobile</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Screenshot</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Amount</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell whitespace-nowrap">Date</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Save</th>
                <th className="px-4 py-3 text-right text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="py-14 text-center text-gray-600 text-sm">No deposit requests found.</td></tr>
              ) : filtered.map(req => {
                const current  = (req.status || '').toLowerCase();
                const selected = statusChanges[req._id] ?? current;
                const changed  = selected !== current;
                return (
                  <tr key={req._id} className="hover:bg-white/[0.015] transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-semibold text-white">{req.user?.username || 'N/A'}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 hidden md:table-cell whitespace-nowrap">
                      {req.user?.mobile || '—'}
                    </td>
                    <td className="px-4 py-3">
                      {req.screenshotPath ? (
                        <button
                          onClick={() => setLightboxUrl(`/${req.screenshotPath}`)}
                          className="group relative w-9 h-9 rounded-lg overflow-hidden border border-white/10 hover:border-cyan-400/40 transition-all"
                        >
                          <img src={`/${req.screenshotPath}`} alt="Screenshot" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Maximize2 size={12} className="text-white" />
                          </div>
                        </button>
                      ) : (
                        <span className="flex items-center gap-1 text-gray-600 text-xs"><ImageIcon size={12} /> None</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-white">${req.amount}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell whitespace-nowrap">
                      {req.createdAt ? new Date(req.createdAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusSelect value={selected} onChange={v => handleStatusChange(req._id, v)} />
                    </td>
                    <td className="px-4 py-3">
                      {changed && (
                        <button
                          onClick={() => handleSave(req)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/25 text-cyan-400 text-xs font-semibold hover:bg-cyan-500/20 transition-all"
                        >
                          <Check size={12} /> Save
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openModal(req.user?._id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/4 border border-white/8 text-gray-400 text-xs hover:text-white hover:bg-white/8 transition-all"
                      >
                        <Info size={12} /> Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setLightboxUrl('')}>
          <div className="relative max-w-3xl mx-4" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightboxUrl('')} className="absolute -top-10 right-0 text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all">
              <X size={18} />
            </button>
            <img src={lightboxUrl} alt="Screenshot" className="max-h-[80vh] w-auto rounded-xl border border-white/10 shadow-2xl" />
          </div>
        </div>
      )}

      {/* Confirm paid dialog */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#0d1526] border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center shrink-0">
                <AlertTriangle size={18} className="text-green-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">Confirm Payment</h3>
                <p className="text-gray-400 text-sm">
                  Mark <span className="text-white font-medium">${confirmAction.request.amount}</span> deposit
                  from <span className="text-white font-medium">{confirmAction.request.user?.username}</span> as Paid?
                  This will credit their balance.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  commitAction(confirmAction.request, confirmAction.nextStatus);
                  setConfirmAction(null);
                }}
                className="flex-1 py-2.5 rounded-xl bg-green-500/12 border border-green-500/25 text-green-400 text-sm font-semibold hover:bg-green-500/20 transition-all"
              >
                Confirm
              </button>
              <button
                onClick={() => setConfirmAction(null)}
                className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-sm font-semibold hover:bg-white/8 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <UserDetailsModal onClose={closeModal} loading={loadingDetails} userDetails={userDetails} />
      )}
    </div>
  );
};

export default DepositRequests;
